import { session } from "@/services/session";
import { in_electron, in_mac } from "@/utils/in";
import type { PeripheralPermissionStatus } from "@/utils/peripherals";

type ElectronMediaAccessStatus = ReturnType<
  typeof import("electron").systemPreferences.getMediaAccessStatus
>;

const IPC_INTERFACE = {
  GET_PERMISSION: "screen-recording:permissions:get",
};

const STORAGE_KEY = "permissions/screen-recording";

const normalizePermissionStatus = (
  status: ElectronMediaAccessStatus
): PeripheralPermissionStatus => (status === "restricted" ? "denied" : status);

const parsePermissionStatus = (
  status: string | null
): PeripheralPermissionStatus => {
  switch (status) {
    case "granted":
    case "denied":
    case "not-determined":
    case "unknown":
    case "unsupported":
      return status;
    default:
      return "unknown";
  }
};

export const screenRecording = {
  initialize() {
    if (!in_electron) return;
    const { ipcMain } = require("electron");
    // IPC handler for checking screen recording permission (macOS only)
    ipcMain.handle(IPC_INTERFACE.GET_PERMISSION, async () => {
      if (process.platform !== "darwin") {
        return "unsupported";
      }
      try {
        const electron: typeof import("electron") = require("electron");
        const { systemPreferences } = electron;
        const status = systemPreferences.getMediaAccessStatus("screen");
        return normalizePermissionStatus(status);
      } catch {
        return "unknown";
      }
    });
  },

  /**
   * Check screen recording permission status
   *
   * - Electron/macOS: Uses IPC to check system permission status in main process
   * - Electron/Windows/Linux: Returns "unsupported" (screen recording permission not applicable)
   * - Web browsers: Returns cached permission status if available, otherwise "unknown"
   *
   * Note: In Web browsers, screen recording permission cannot be checked programmatically without side effects.
   * The cached status is updated when getDisplayMedia() is successfully called (see savePermission).
   *
   * @returns The current screen recording permission status
   */
  getPermission: async (): Promise<PeripheralPermissionStatus> => {
    if (in_electron) {
      if (in_mac()) {
        // macOS: Check permission via IPC (must be done in main process)
        try {
          const { ipcRenderer } = require("electron");
          return await ipcRenderer.invoke(IPC_INTERFACE.GET_PERMISSION);
        } catch {
          return "unknown";
        }
      } else {
        // Windows/Linux: Screen recording permission not applicable
        return "unsupported";
      }
    } else {
      // Web browser: Check cached permission status from previous getDisplayMedia() calls
      // If no cached status exists, return "unknown" since we can't check without side effects
      try {
        return parsePermissionStatus(session.store.getRaw(STORAGE_KEY, null));
      } catch {
        return "unknown";
      }
    }
  },

  /**
   * Save screen recording permission status
   * Should be called when getDisplayMedia() succeeds to cache the permission state
   *
   * @param status The permission status to save
   */
  savePermission: (status: PeripheralPermissionStatus): void => {
    try {
      session.store.setRaw(STORAGE_KEY, status);
    } catch (error) {
      window.console.warn(
        "Failed to save screen recording permission status",
        error
      );
    }
  },
};
